/**
 * atomberg-delivery-check.js
 *
 * Pincode field with Check, and a result row (delivery date, or not serviceable) with Change.
 *
 * On the PDP it checks the selected variant itself; the APIs depend on data attributes on the root:
 *   – Clickpost + direct serviceability API:
 *       requires_serviceability_check product metafield = true, backend flag off
 *   – Clickpost only: no serviceability check required, backend flag off
 *   – Backend only (one call): shop.metafields.atomberg.edd_use_backend_api = true —
 *       folds the serviceability check into the same request when
 *       requires_serviceability_check is also true; see callBackendEdd()
 *
 * In the cart (no SKU map) it only handles input, Check and Change — atomberg-cart-edd.js checks
 * every item and renders the result through the helpers exposed as root._adcV2.
 *
 * On the PDP it broadcasts its state as `atomberg:delivery-state` for sibling
 * blocks (free installation, nearby devices) — see atomberg-delivery-state.js.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'atomberg_pincode';
  var EDD_CACHE_KEY = 'atomberg_edd_cache';
  var EDD_TTL_MS = 10 * 60 * 1000;
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** @returns {string} */
  function getSavedPincode() {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  }

  /**
   * @param {string} pincode
   * @param {boolean} [silent] - Skip the atomberg:pincode-saved dispatch. Used
   *   by automatic re-verification (page-load restore, variant switch) so it
   *   doesn't force atomberg-cart-edd.js to run a real network check every
   *   time the PDP silently re-checks an already-saved pincode — the cart
   *   should only check when it's actually opened or explicitly told to.
   */
  function savePincode(pincode, silent) {
    try {
      localStorage.setItem(STORAGE_KEY, pincode);
    } catch (_) {}
    if (silent) return;
    // Notify atomberg-cart-edd.js to refresh EDD display in the cart.
    document.dispatchEvent(
      new CustomEvent('atomberg:pincode-saved', { detail: { pincode: pincode } })
    );
  }

  /**
   * Read a fresh EDD cache entry for this SKU+pincode, or null when absent/expired.
   * @param {string} sku
   * @param {string} pincode
   * @returns {{minMs: number|null, maxMs: number|null} | null}
   */
  function readEddCache(sku, pincode) {
    if (!sku || !pincode) return null;
    try {
      var cache = JSON.parse(localStorage.getItem(EDD_CACHE_KEY) || '{}');
      var hit = cache[sku + '_' + pincode];
      if (hit && Date.now() - hit.ts < EDD_TTL_MS) return hit;
    } catch (_) {}
    return null;
  }

  /**
   * Write EDD result to the shared EDD cache (10-minute TTL, keyed by SKU+pincode).
   * @param {string} sku
   * @param {string} pincode
   * @param {number|null} minMs - absolute timestamp for earliest delivery
   * @param {number|null} maxMs - absolute timestamp for latest delivery
   */
  function writeEddCache(sku, pincode, minMs, maxMs) {
    if (!sku || !pincode) return;
    try {
      var cache = JSON.parse(localStorage.getItem(EDD_CACHE_KEY) || '{}');
      cache[sku + '_' + pincode] = { minMs: minMs, maxMs: maxMs, ts: Date.now() };
      // Prune entries older than TTL to avoid unbounded growth.
      var cutoff = Date.now() - EDD_TTL_MS;
      Object.keys(cache).forEach(function (k) {
        if (cache[k].ts < cutoff) delete cache[k];
      });
      localStorage.setItem(EDD_CACHE_KEY, JSON.stringify(cache));
    } catch (_) {}
  }

  /** @param {string} value */
  function isValidPincode(value) {
    return /^\d{6}$/.test(value);
  }

  /**
   * "22 Sep - 23 Sep", or "22 Sep" when both ends fall on the same day; '' when neither is known.
   * @param {number|null} minMs
   * @param {number|null} maxMs
   * @returns {string}
   */
  function fmtDateRange(minMs, maxMs) {
    var lo = minMs != null ? minMs : maxMs;
    var hi = maxMs != null ? maxMs : minMs;
    if (lo == null || hi == null) return '';
    var loDate = new Date(lo);
    var hiDate = new Date(hi);
    var from = loDate.getDate() + ' ' + MONTHS[loDate.getMonth()];
    var to = hiDate.getDate() + ' ' + MONTHS[hiDate.getMonth()];
    return from === to ? from : from + ' - ' + to;
  }

  /**
   * @param {HTMLElement} root
   */
  function init(root) {
    var clickpostUrl = root.getAttribute('data-adc-clickpost-url') || '';
    var serviceabilityUrl = root.getAttribute('data-adc-serviceability-url') || '';
    var productId = root.getAttribute('data-adc-product-id') || '';
    var requiresServiceability = root.hasAttribute('data-adc-requires-serviceability');
    var useBackendEdd = root.getAttribute('data-adc-use-backend-edd') === 'true';
    var backendEddUrl = root.getAttribute('data-adc-backend-edd-url') || '';

    // ── SKU map: {"variantId": "SKU"} rendered by the block ──────────────────
    /** @type {Record<string, string>} */
    var skuMap = {};
    var skuMapEl = root.querySelector('[data-adc-sku-map]');
    if (skuMapEl) {
      try {
        skuMap = JSON.parse(skuMapEl.textContent || '{}');
      } catch (_) {}
    }

    // Resolve initial variant from the URL ?variant= param, falling back to the first map entry.
    var currentVariantId = (function () {
      var urlVid = new URLSearchParams(window.location.search).get('variant');
      if (urlVid && skuMap[urlVid]) return urlVid;
      return Object.keys(skuMap)[0] || '';
    })();
    var currentSku = skuMap[currentVariantId] || '';

    /** @type {Record<string, {variant?: string, title?: string}>} */
    var variantInfo = {};
    var variantInfoEl = root.querySelector('[data-adc-variant-info]');
    if (variantInfoEl) {
      try {
        variantInfo = JSON.parse(variantInfoEl.textContent || '{}');
      } catch (_) {}
    }
    var productName = root.getAttribute('data-adc-product-name') || '';

    // ── DOM refs ──────────────────────────────────────────────────────────────
    var summary = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-summary]'));
    var editBtn = root.querySelector('[data-adc-edit]');
    var formWrap = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-form-wrap]'));
    var input = /** @type {HTMLInputElement|null} */ (root.querySelector('[data-adc-input]'));
    var actionBtn = root.querySelector('[data-adc-action]');
    var messageEl = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-message]'));

    // ── Result row refs ───────────────────────────────────────────────────────
    var v2Label = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-v2-label]'));
    var v2Ok = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-v2-ok]'));
    var v2Bad = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-v2-bad]'));
    var v2OkText = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-v2-ok-text]'));
    var v2BadText = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-v2-bad-text]'));
    var v2Date = /** @type {HTMLElement|null} */ (root.querySelector('[data-adc-v2-date]'));
    var v2BadDefault = v2BadText ? v2BadText.textContent || '' : '';

    // ── UI helpers ────────────────────────────────────────────────────────────
    function showSummary() {
      if (summary) summary.hidden = false;
      if (formWrap) formWrap.hidden = true;
    }

    function showForm() {
      if (summary) summary.hidden = true;
      if (formWrap) formWrap.hidden = false;
      if (input) input.focus();
    }

    /**
     * @param {string} text
     * @param {'error' | ''} type
     */
    function setMessage(text, type) {
      if (!messageEl) return;
      messageEl.textContent = text;
      messageEl.className =
        'atomberg-delivery-check__message' +
        (type ? ' atomberg-delivery-check__message--' + type : '');
      messageEl.hidden = !text;
    }

    /**
     * Writes a "{pincode}"-templated label, keeping the pincode in its own span
     * so the not-serviceable state can ink it red without innerHTML.
     * @param {string} tpl
     * @param {string} pincode
     * @param {boolean} danger
     */
    function setV2Label(tpl, pincode, danger) {
      if (!v2Label) return;
      v2Label.textContent = '';
      var parts = String(tpl).split('{pincode}');
      v2Label.appendChild(document.createTextNode(parts[0]));
      if (parts.length > 1) {
        var pin = document.createElement('span');
        if (danger) pin.className = 'atomberg-delivery-check__v2-danger-ink';
        pin.textContent = pincode;
        v2Label.appendChild(pin);
        v2Label.appendChild(document.createTextNode(parts.slice(1).join('{pincode}')));
      }
    }

    /**
     * @param {string} pincode
     * @param {number|null} eddMinMs
     * @param {number|null} eddMaxMs
     */
    function setV2Checked(pincode, eddMinMs, eddMaxMs) {
      setV2Label(root.getAttribute('data-adc-v2-label-checked') || '', pincode, false);
      var range = fmtDateRange(eddMinMs, eddMaxMs);
      if (v2Date) v2Date.textContent = range;
      if (v2OkText) v2OkText.hidden = !range;
      if (v2BadText) v2BadText.hidden = true;
      // Hiding the tick too left the row holding nothing but a Change button
      if (v2Ok) v2Ok.hidden = false;
      if (v2Bad) v2Bad.hidden = true;
    }

    /**
     * @param {string} pincode
     * @param {string} [text] - Replaces the default row text; the cart names the undeliverable items here
     */
    function setV2NotServiceable(pincode, text) {
      setV2Label(root.getAttribute('data-adc-v2-label-unserviceable') || '', pincode, true);
      if (v2OkText) v2OkText.hidden = true;
      if (v2BadText) {
        v2BadText.textContent = text || v2BadDefault;
        v2BadText.hidden = false;
      }
      if (v2Ok) v2Ok.hidden = true;
      if (v2Bad) v2Bad.hidden = false;
      showSummary();
    }

    function resetV2() {
      setV2Label(root.getAttribute('data-adc-v2-label-empty') || '', '', false);
    }

    /**
     * Broadcasts the check's state to sibling PDP blocks (read through
     * atomberg-delivery-state.js). Settled states are also mirrored on the root,
     * so a block that binds after the check has resolved still starts in step.
     * @param {'typed'|'serviceable'|'unserviceable'|'reset'|'unavailable'} state
     * @param {string} pincode
     */
    function emitState(state, pincode) {
      // Cart widgets have no SKU map, and nothing outside the PDP listens
      if (!skuMapEl) return;
      if (state !== 'typed') {
        var settled = state === 'reset' ? '' : state;
        root.setAttribute('data-adc-state', settled);
        root.setAttribute('data-adc-state-pincode', settled ? pincode : '');
      }
      root.dispatchEvent(
        new CustomEvent('atomberg:delivery-state', {
          detail: { state: state, pincode: pincode },
          bubbles: true,
        })
      );
    }

    /**
     * Reports a settled check to atomberg-moengage-datalayer.js, which fires
     * check_delivery_time. Every settled check reports, including the ones the
     * shopper did not start — a variant switch refetches without a tap, and that
     * result is as real as a tapped one.
     * @param {string} pincode
     * @param {boolean} serviceable
     * @param {boolean} errored - The API never answered, so serviceability is unknown
     * @param {boolean} byShopper - The Check button was pressed, rather than a variant
     *   switch, a page load or a cart-side save driving this
     */
    function emitCheckDelivery(pincode, serviceable, errored, byShopper) {
      var info = variantInfo[currentVariantId] || {};
      // Shopify's placeholder on products with no real variants — /cart.js sends null
      // for those, so blanking it here keeps the PDP and the cart reporting the same thing
      var shopifyVariant = info.variant === 'Default Title' ? '' : info.variant || '';
      document.dispatchEvent(
        new CustomEvent('atomberg:check-delivery', {
          detail: {
            pincode: pincode,
            // checkDelivery only ever runs on the PDP — the cart's Check returns
            // before it and reports through atomberg-cart-edd.js instead
            page_type: 'pdp',
            // Lets a dashboard count one row per shopper action; every refetch still reports
            trigger: byShopper ? 'user' : 'background',
            is_serviceable: serviceable,
            error: errored,
            product_name: productName,
            product_variant: shopifyVariant,
            variant_title: info.title || '',
          },
          bubbles: true,
        })
      );
    }

    // ── API calls ─────────────────────────────────────────────────────────────
    /**
     * POST to Clickpost WMS Unified Serviceability API.
     * @param {string} pincode
     * @param {string} sku
     * @returns {Promise<{serviceable: boolean, eddMinMs: number|null, eddMaxMs: number|null, fromCache?: boolean}>}
     */
    function callClickpost(pincode, sku) {
      if (!clickpostUrl || !sku)
        return Promise.resolve({ serviceable: true, eddMinMs: null, eddMaxMs: null });
      return fetch(clickpostUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sku: sku, drop_pincode: pincode, wms_code: 'vinculum' }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          var none = { serviceable: false, eddMinMs: null, eddMaxMs: null };
          var meta = (data && data.meta) || {};
          if (!meta.success) return none;
          var result = (data && data.result) || {};
          var accounts = result.serviceable_accounts || [];
          if (!accounts.length) return none;
          var ok = accounts.some(function (/** @type {any} */ acc) {
            var s = (acc && acc.serviceable) || {};
            return s.PREPAID === true || s.COD === true;
          });
          if (!ok) return none;
          var edd = result.edd || {};
          var minDays = edd.min != null ? edd.min : edd.max;
          var maxDays = edd.max != null ? edd.max : edd.min;
          var now = Date.now();
          var eddMinMs = minDays != null ? now + minDays * 86400000 : null;
          var eddMaxMs = maxDays != null ? now + maxDays * 86400000 : null;
          return {
            serviceable: true,
            eddMinMs: eddMinMs,
            eddMaxMs: eddMaxMs,
          };
        });
    }

    /**
     * POST to the backend EDD endpoint — replaces callClickpost when the
     * shop.metafields.atomberg.edd_use_backend_api flag is on. When
     * includeServiceability is true (product flagged
     * requires_serviceability_check), the pan-India serviceability gate is
     * folded into this same request via the item's own checkServiceability
     * flag instead of a separate callServiceabilityApi round trip — the
     * result's `serviceable` field carries that verdict back as
     * backendServiceable.
     * @param {string} pincode
     * @param {string} sku
     * @param {boolean} [includeServiceability]
     * @returns {Promise<{serviceable: boolean, eddMinMs: number|null, eddMaxMs: number|null, backendServiceable: boolean|null}>}
     */
    function callBackendEdd(pincode, sku, includeServiceability) {
      if (!backendEddUrl || !sku)
        return Promise.resolve({
          serviceable: true,
          eddMinMs: null,
          eddMaxMs: null,
          backendServiceable: null,
        });
      /** @type {any} */
      var item = { sku: sku };
      if (productId) item.productId = productId;
      if (includeServiceability) item.checkServiceability = true;
      return fetch(backendEddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item], pincode: pincode }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          var none = {
            serviceable: false,
            eddMinMs: null,
            eddMaxMs: null,
            backendServiceable: null,
          };
          var result = ((data && data.results) || [])[0];
          if (!result) return none;
          var backendServiceable = result.serviceable != null ? result.serviceable : null;
          if (result.status !== 'ok') {
            return {
              serviceable: false,
              eddMinMs: null,
              eddMaxMs: null,
              backendServiceable: backendServiceable,
            };
          }
          var now = Date.now();
          var eddMinMs = result.eddMinDays != null ? now + result.eddMinDays * 86400000 : null;
          var eddMaxMs = result.eddMaxDays != null ? now + result.eddMaxDays * 86400000 : null;
          return {
            serviceable: true,
            eddMinMs: eddMinMs,
            eddMaxMs: eddMaxMs,
            backendServiceable: backendServiceable,
          };
        });
    }

    /**
     * Clickpost (or the backend EDD endpoint, per the feature flag) with the
     * 10-minute EDD cache in front — cache hit skips the API call.
     * @param {string} pincode
     * @param {string} sku
     * @returns {Promise<{serviceable: boolean, eddMinMs: number|null, eddMaxMs: number|null, fromCache?: boolean}>}
     */
    function callClickpostCached(pincode, sku) {
      var hit = readEddCache(sku, pincode);
      if (hit) {
        return Promise.resolve({
          serviceable: true,
          eddMinMs: hit.minMs,
          eddMaxMs: hit.maxMs,
          fromCache: true,
        });
      }
      return useBackendEdd ? callBackendEdd(pincode, sku) : callClickpost(pincode, sku);
    }

    /**
     * GET the direct product-level serviceability check.
     * @param {string} pincode
     * @returns {Promise<boolean>}
     */
    function callServiceabilityApi(pincode) {
      if (!serviceabilityUrl || !productId) return Promise.resolve(true);
      var url =
        serviceabilityUrl +
        '?product_id=' +
        encodeURIComponent(productId) +
        '&pincode=' +
        encodeURIComponent(pincode);
      return fetch(url)
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          return !!(data && data.serviceable);
        });
    }

    /**
     * Run serviceability checks and update UI.
     *
     * @param {string} pincode
     * @param {string} sku
     * @param {{showLoader?: boolean}} [opts]
     *   showLoader - Disable the button and show "Checking…" while the request runs.
     */
    function checkDelivery(pincode, sku, opts) {
      var showLoader = !!(opts && opts.showLoader);
      // No API configured on this store — nothing to verify against, so accept the pincode.
      if (!clickpostUrl && !serviceabilityUrl && !backendEddUrl) {
        savePincode(pincode, !showLoader);
        setMessage('', '');
        showSummary();
        return;
      }

      var btn = /** @type {HTMLButtonElement|null} */ (actionBtn);
      if (showLoader && btn) {
        btn.disabled = true;
        btn.textContent = 'Checking…';
      }

      var needsServiceability = requiresServiceability && !!productId;

      /** @type {Promise<any>} */
      var calls;
      if (useBackendEdd && needsServiceability && backendEddUrl) {
        // Merged path — serviceability must never be cached, so this always
        // hits the network fresh (same invariant as the direct-API branch below).
        calls = callBackendEdd(pincode, sku, true).then(function (cp) {
          var svc = cp.backendServiceable == null ? true : cp.backendServiceable;
          return [cp, svc];
        });
      } else if (needsServiceability && serviceabilityUrl) {
        // Serviceability is the blocking check — always called fresh, never cached.
        calls = Promise.all([callClickpostCached(pincode, sku), callServiceabilityApi(pincode)]);
      } else {
        calls = callClickpostCached(pincode, sku).then(function (cp) {
          return [cp, true];
        });
      }

      // Capture at call time — currentSku may change before the async fetch resolves.
      var capturedSku = sku;

      calls
        .then(function (results) {
          var cp = results[0];
          var svc = results[1];
          if (cp.serviceable && svc) {
            setMessage('', '');
            showSummary();
            if (!cp.fromCache) writeEddCache(capturedSku, pincode, cp.eddMinMs, cp.eddMaxMs);
            // Runs inside the .then, so a throw here would land in the .catch
            // below and show a delivery failure the shopper doesn't actually have
            try {
              setV2Checked(pincode, cp.eddMinMs, cp.eddMaxMs);
              emitState('serviceable', pincode);
            } catch (_) {}
            // After emitState: savePincode dispatches atomberg:pincode-saved synchronously, and
            // this widget's own listener skips it by comparing against the state attribute above
            savePincode(pincode, !showLoader);
            emitCheckDelivery(pincode, true, false, showLoader);
          } else {
            setV2NotServiceable(pincode);
            emitState('unserviceable', pincode);
            // Stored even though this product is refused: the cart and checkout guard read the same
            // key and run their own per-item check, so the shopper sees one pincode everywhere.
            // After emitState for the same reason as the serviceable branch above
            savePincode(pincode, !showLoader);
            emitCheckDelivery(pincode, false, false, showLoader);
          }
        })
        .catch(function () {
          setMessage("Sorry, we couldn't fetch pincode details. Please try again.", 'error');
          emitCheckDelivery(pincode, false, true, showLoader);
        })
        .finally(function () {
          if (showLoader && btn) {
            btn.disabled = false;
            btn.textContent = 'Check';
          }
        });
    }

    // ── Restore saved pincode ─────────────────────────────────────────────────
    var saved = getSavedPincode();
    var isCartContext = !skuMapEl;
    // PDP only: pre-fill the input from a previous visit. The cart is the
    // final serviceability checkpoint — its input always starts blank, with
    // no value and no API call, no matter what's saved from the PDP. The
    // shopper must type the pincode and press Check themselves every time.
    if (saved && input && !isCartContext) input.value = saved;

    // ── Edit ──────────────────────────────────────────────────────────────────
    function beginEdit() {
      setMessage('', '');
      resetV2();
      emitState('reset', '');
      showForm();
      // Change is specified as "back to the empty state" — the field only. The stored pincode
      // survives so a shopper who changes their mind and walks away keeps the one they had
      if (input) input.value = '';
    }

    if (editBtn) editBtn.addEventListener('click', beginEdit);

    // ── Input field ───────────────────────────────────────────────────────────
    if (input) {
      input.addEventListener('input', function () {
        if (!input) return;
        input.value = input.value.replace(/\D/g, '').slice(0, 6);
        setMessage('', '');
        if (isValidPincode(input.value)) emitState('typed', input.value);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (actionBtn) /** @type {HTMLElement} */ (actionBtn).click();
        }
      });
    }

    // ── Check button ──────────────────────────────────────────────────────────
    if (actionBtn) {
      actionBtn.addEventListener('click', function () {
        var pincode = input ? input.value : '';
        if (!isValidPincode(pincode)) {
          setMessage('Please enter a valid 6-digit pincode.', 'error');
          if (input) input.focus();
          return;
        }
        if (isCartContext) {
          // Cart: atomberg-cart-edd.js owns the result UI. Hold the loader here —
          // it resets the button when its API calls settle.
          var btnEl = /** @type {HTMLButtonElement|null} */ (actionBtn);
          if (btnEl) {
            btnEl.disabled = true;
            btnEl.textContent = 'Checking…';
          }
          // Tells atomberg-cart-edd.js this save is the cart's own Check, so it runs the
          // per-item fan-out. A save from the PDP leaves the pincode stored instead.
          document.dispatchEvent(
            new CustomEvent('atomberg:cart-check-requested', {
              detail: { pincode: pincode },
              bubbles: true,
            })
          );
          savePincode(pincode);
          return;
        }
        checkDelivery(pincode, currentSku, { showLoader: true });
      });
    }

    // ── Variant switch — re-check with new SKU, errors visible ───────────────
    // atomberg-product-variant-picker.js dispatches 'atomberg:pdp-variant-change' (bubbles: true)
    document.addEventListener('atomberg:pdp-variant-change', function (/** @type {any} */ e) {
      var variant = (e.detail || {}).variant;
      if (!variant) return;
      var vid = String(variant.id);
      // Use `|| currentSku` to keep currentSku typed as string (avoids TS widening to string|undefined).
      currentSku = skuMap[vid] || currentSku;
      if (skuMap[vid]) currentVariantId = vid;

      // Hide widget and collapse its Horizon block wrapper for OOS variants.
      /** @type {HTMLElement} */ (root).hidden = !variant.available;
      var blockWrapper = /** @type {HTMLElement|null} */ (root.closest('.shopify-block'));
      if (blockWrapper) blockWrapper.style.display = variant.available ? '' : 'none';

      if (!variant.available) {
        emitState('unavailable', '');
        return;
      }
      var pincode = getSavedPincode() || (input ? input.value : '');
      if (isValidPincode(pincode) && currentSku) checkDelivery(pincode, currentSku);
      else emitState('reset', '');
    });

    // atomberg-cart-edd.js decides the cart's verdict across every item, then renders it through these
    if (isCartContext) {
      /** @type {any} */ (root)._adcV2 = {
        checked: setV2Checked,
        notServiceable: setV2NotServiceable,
        reset: resetV2,
      };
    }

    // ── Pincode changed elsewhere ─────────────────────────────────────────────
    // The cart owns the same storage key, so a check or Change made there has to reach the PDP
    // behind the drawer. Cart widgets are skipped — atomberg-cart-edd.js already drives those.
    if (!isCartContext) {
      // Held while the drawer is open: the shopper is looking at the cart, so re-checking this
      // product mid-session spends a call per pincode they try. Runs once, on close.
      var deferredPincode = '';
      var drawerOpen = false;

      function syncFromCart(/** @type {string} */ next) {
        if (!isValidPincode(next) || next === root.getAttribute('data-adc-state-pincode')) return;
        if (input) input.value = next;
        if (currentSku) checkDelivery(next, currentSku);
      }

      document.addEventListener('atomberg:pincode-saved', function (/** @type {any} */ e) {
        var next = ((e && e.detail) || {}).pincode || '';
        if (drawerOpen) {
          deferredPincode = next;
          return;
        }
        syncFromCart(next);
      });

      // dialog:open / dialog:close fire on cart-drawer-component itself and do not bubble
      customElements.whenDefined('cart-drawer-component').then(function () {
        document.querySelectorAll('cart-drawer-component').forEach(function (el) {
          el.addEventListener('dialog:open', function () {
            drawerOpen = true;
          });
          el.addEventListener('dialog:close', function () {
            drawerOpen = false;
            var pending = deferredPincode;
            deferredPincode = '';
            if (pending) syncFromCart(pending);
          });
        });
      });
    }

    // ── Initial state ─────────────────────────────────────────────────────────
    // Cart context: skip auto-summary — atomberg-cart-edd.js shows summary only when EDD is found.
    // The row is API-populated, so it stays hidden until that check lands
    if (saved && !isCartContext && currentSku) checkDelivery(saved, currentSku);
  }

  function initAll() {
    document.querySelectorAll('[data-atomberg-delivery-check]').forEach(function (el) {
      var root = /** @type {any} */ (el);
      if (root._adcBound) return;
      root._adcBound = true;
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:load', initAll);

  // morphSection fires synchronously after cart:update. setTimeout(0) pushes our re-init into the
  // next tick — after the morph — so any node morphdom replaced entirely (losing _adcBound) is re-bound.
  document.addEventListener('cart:update', function () {
    setTimeout(initAll, 0);
  });

  // dialog:open fires on cart-drawer-component (not bubbling); no cart:update fires on a plain open,
  // so this re-binds a drawer widget that was swapped in without one.
  customElements.whenDefined('cart-drawer-component').then(function () {
    document.querySelectorAll('cart-drawer-component').forEach(function (el) {
      el.addEventListener('dialog:open', initAll);
    });
  });
})();
